import { calcInitialWidth, mediaQueriesBP } from "@iewcasia/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    mainImage = "mainImage",
    background = "background",
    textColor = "textColor",
    title = "title",
    description = "description",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",

    displayMainImage = "Image",
    displayBackground = "Content background",
    displayTextColor = "Content text color",
    displayTitle = "Title",
    displayDescription = "Description",
    displayButtonLabel = "CTA",
    displayButtonLink = "CTA link",
    displayButtonVariant = "CTA Variant",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.mainImage]: string;
        [fields.background]: string;
        [fields.textColor]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
    };
}

export const mapStateToProps = () => ({});

export type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps>;

export interface HomePageHeroWidgetStyles {
    wrapper?: InjectableCss;
    heroContainer?: InjectableCss;
    heroContent?: InjectableCss;
    heroButton?: ButtonProps;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
}

const heightComponent = "405px";

export const homePageHeroWidgetStyles: HomePageHeroWidgetStyles = {
    wrapper: {
        css: css`
            width: ${calcInitialWidth};
            position: relative;
            background-color: transparent;

            left: 50%;
            right: 50%;
            margin: 0 -50vw;
        `,
    },
    heroContainer: {
        css: css`
            max-width: 100%;
            width: 100%;
            height: auto;
            display: flex;
            flex-direction: column;
            margin: -2px 0;

            ${mediaQueriesBP.minMD} {
                width: 100%;
                max-width: 1440px;
                flex-direction: row;
                margin: 0 auto;
                justify-content: space-around;
                align-items: stretch;
            }
        `,
    },
    heroContent: {
        css: css`
            padding: 20px 25px;
            width: 100%;
            display: flex;
            flex-direction: column;
            min-height: 300px;

            ${mediaQueriesBP.minMD} {
                min-height: ${heightComponent};
                max-width: 50%;
                padding: 40px 30px 40px;
                justify-content: flex-start;
            }

            ${mediaQueriesBP.minXLG} {
                padding: 50px 280px 50px 30px;
            }
        `,
    },

    title: {
        forwardAs: "h1",
        variant: "h2",
        css: css`
            text-align: left;
        `,
    },

    description: {
        css: css`
            padding: 20px 0;
            font-size: 18px;
            line-height: 27px;

            ${mediaQueriesBP.minLG} {
                font-size: 20px;
                line-height: 30px;
            }
        `,
    },
    heroButton: {
        mergeCss: true,
        css: css`
            max-width: 60%;

            ${mediaQueriesBP.minLG} {
                max-width: fit-content;
                min-height: 46px;
                min-width: 200px;
            }
        `,
    },
};

const styles = homePageHeroWidgetStyles;

const onClick = (history: History, url?: string) => {
    if (url) {
        history.push(url);
    }
};

export const HomePageHeroWidget = ({ id, fields, history }: Props) => {
    const link = useGetLink(fields.buttonLink);
    const heroImage = css`
        background-image: url("${fields.mainImage}");
        display: flex;
        flex-direction: column;
        justify-content: center;
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;
        width: 100%;
        min-height: 290px;

        ${mediaQueriesBP.minMD} {
            height: auto;
            width: 50%;
        }
    `;

    const heroConent = css`
        ${styles.heroContent?.css}
        background-color: ${fields.background};
    `;

    const titleStyle: TypographyPresentationProps = {
        ...styles.title,
        color: fields.textColor,
    };

    const descriptionStyle: TypographyPresentationProps = {
        ...styles.description,
        color: fields.textColor,
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.heroContainer}>
                <StyledWrapper css={heroImage}></StyledWrapper>

                <StyledWrapper css={heroConent}>
                    <Typography {...titleStyle}>{fields.title}</Typography>
                    <Typography {...descriptionStyle}>{fields.description}</Typography>
                    {link.url && (
                        <Button
                            {...styles.heroButton}
                            variant={fields.buttonVariant as any}
                            onClick={() => onClick(history, link?.url)}
                        >
                            {fields.buttonLabel}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(HomePageHeroWidget)) as any,
    definition: {
        displayName: "Home Page Hero",
        group: "Verndale Box" as any,
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.mainImage,
                displayName: fields.displayMainImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.background,
                displayName: fields.displayBackground,
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.textColor,
                displayName: fields.displayTextColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#333333",
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.description,
                displayName: fields.displayDescription,
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
            },
            {
                name: fields.buttonLink,
                displayName: fields.displayButtonLink,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: false,
                dependentFields: [fields.buttonLabel],
            },
            {
                name: fields.buttonLabel,
                displayName: fields.displayButtonLabel,
                editorTemplate: "TextField",
                defaultValue: "Read more",
                fieldType: "Translatable",
                isVisible: item => item.fields.buttonLink != null && item.fields.buttonLink.value,
            },
            {
                name: fields.buttonVariant,
                displayName: fields.displayButtonVariant,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                isVisible: item => item.fields.buttonLink != null && item.fields.buttonLink.value,
            },
        ],
    },
};

export default widgetModule;
